import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isMobileOnly } from 'react-device-detect';
import ReactStars from 'react-stars';
import { AutoSizer, Table, Column, SortDirection } from 'react-virtualized';
import { FaSort } from 'react-icons/fa';
import { BsInfoCircle, BsChatLeftFill, BsClipboard2CheckFill } from 'react-icons/bs';
import debounce from 'lodash.debounce';
import { formatShortDate, CustomTooltip } from '../../../utils/helpers';
import { formatTime, resolveCategory, getIconBasedOnCat, rowHighlightStyle, ROW_HEIGHT, TABLE_WIDTH } from '../utils/reportHelpers';
import HtmlTooltip from './HtmlTooltip';
import RatingModal from './RatingModal';
import Filterstop from './Filterstop';
import gigpig from '../../../images/manager-report/gigpig.svg';

const ReportTableRow = ({
    data,
    reportData,
    featureFlags,
    filterValues,
    setFilterValues,
    costingVisibleCols,
    setCostingVisibleCols,
    columnConfig,
    onRatingSubmit,
    onSignoffModal,
    onQueryFormOpen,
    onDataChange,
    visibleDataRef,
    prevFilteredDataRef,
    updateLineItemRef,
    setLoading,
    DataAfterFilter,
    mobileFilters,
}) => {
    const [showRatingFeedback, setShowRatingFeedback] = useState(false);
    const [tableData, setTableData] = useState(data);
    const [feedbackdata, setfeedbackdata] = useState({ rating: null, lineid: null, rl: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASC);
    const hasOpenedModalRef = useRef(false);
    const showQueryFormPopup = false; 

    const showCol = (key) => costingVisibleCols.includes(key);

    const ShowFeedbackmodel = (rating, lineId, Rl) => {
        setShowRatingFeedback(true);
        setfeedbackdata(prevState => ({
            ...prevState,
            rating: rating,
            lineid: lineId,
            rl: Rl
        }));
    };

    const ratingModalHide = () => setShowRatingFeedback(false);

    // Preserve local rating/signoff state across parent data refreshes
    useEffect(() => {
        if (!tableData.length) {
            setTableData(data);
        } else {
            const previousData = prevFilteredDataRef.current || [];
            const updatedData = data.map(dataItem => {
                const existingItem = previousData.find(dd => dd.rw === dataItem.rw);
                return existingItem || dataItem;
            });
            setTableData(updatedData);
        }
    }, [data]);

    const ratingChanged = (newRating, lineId, rl) => {
        if (lineId !== 0) {
            setTableData(prevData => {
                const newData = prevData.map(item => {
                    if (item.lineId === lineId) {
                        return { ...item, rating: newRating };
                    }
                    return item;
                });
                prevFilteredDataRef.current = newData;
                return newData;
            });
            if (newRating === 1 || newRating === 5) {
                ShowFeedbackmodel(newRating, lineId, rl);
            } else if (newRating >= 2 && newRating <= 4) {
                onRatingSubmit(newRating, lineId, rl, null);
            }
        }
    };

    const handleLineItemUpdate = (lineid, ratings, signatures) => {
        if (lineid !== 0) {
            setTableData(prevData => {
                const newData = prevData.map(item => {
                    if (item.lineId === lineid) {
                        return { ...item, requireApproval: false, rating: ratings, managerSignature: signatures };
                    }
                    return item;
                });
                prevFilteredDataRef.current = newData;
                return newData;
            });
        }
    };
    updateLineItemRef.current = handleLineItemUpdate;

    const infoCellRenderer = ({ rowData }) => {
        if (rowData.infoPanel && rowData.infoPanel.trim() !== '') {
            return (
                <HtmlTooltip content={rowData.infoPanel}>
                    <BsInfoCircle
                        size={16}
                        style={{ color: '#0066cc', cursor: 'help' }}
                    />
                </HtmlTooltip>
            );
        }
        return null;
    };

    const categoryCellRenderer = ({ rowData }) => {
        const category = resolveCategory(rowData.cat, rowData.rowHighlightColour);
        return (
            <CustomTooltip content={category}>
                {getIconBasedOnCat(category)}
            </CustomTooltip>
        );
    };

    const ratingCellRenderer = ({ rowData }) => {
        if (rowData.lineId !== 0) {
            return (
                <ReactStars
                    count={5}
                    onChange={(newRating) => ratingChanged(newRating, rowData.lineId, rowData.rl)}
                    value={rowData.rating}
                    size={17}
                    half={false}
                    color2="#ffc107"
                    color1="#999"
                />
            );
        }
        return null;
    };

    const dateCellRenderer = ({ cellData }) => {
        return <div>{formatShortDate(cellData)}</div>;
    };
    const timeCellRenderer = ({ cellData }) => {
        return <div>{formatTime(cellData)}</div>;
    };
    const nettCostCellRenderer = ({ cellData }) => {
        return <div> &pound; {cellData.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}</div>;
    };
    const challengeCellRenderer = ({ rowData }) => {
        if (rowData.underQuery != null) {
            return (
                <span style={{ color: rowData.underQueryColour }}>
                    <strong>{rowData.underQuery}</strong>
                </span>
            );
        } else if (rowData.challengeLink) {
            return (
                <BsChatLeftFill
                    size={18}
                    color="#d00078"
                    style={{ cursor: 'pointer' }}
                    title="Challenge"
                    onClick={() => onQueryFormOpen(rowData)}
                />
            );
        } else if (rowData.credited) {
            return (
                <div>
                    <div>Credited</div>
                    <div>{formatShortDate(rowData.credited)}</div>
                </div>
            );
        } else {
            return null;
        }
    };

    const gigPigCellRender = ({ rowData }) => {
        if (rowData.underQuery != null) {
            return (
                <span style={{ color: rowData.underQueryColour }}>
                    <strong>{rowData.underQuery}</strong>
                </span>
            );
        }
        if (rowData.gigPig === true) {
            return <img style={{ height: 'auto', width: '25%', margin: '0 2rem' }} src={gigpig} alt="GigPig" />;
        }
        if (rowData.requireApproval === true) {
            return (
                <BsClipboard2CheckFill
                    size={20}
                    color="#D00078"
                    style={{ cursor: 'pointer' }}
                    title="Approve/Challenge"
                    onClick={() => onSignoffModal(rowData)}
                />
            );
        }
        if (rowData.challengeLink) {
            return (
                <BsClipboard2CheckFill
                    size={18}
                    color="#d00078"
                    style={{ cursor: 'pointer' }}
                    title="Challenge"
                    onClick={() => onQueryFormOpen(rowData)}
                />
            );
        }
        if (rowData.credited) {
            return (
                <div>
                    <div>Credited</div>
                    <div>{formatShortDate(rowData.credited)}</div>
                </div>
            );
        }
        return null;
    };

    const notesCellRenderer = ({ rowData }) => (
        <div title={rowData.notes}>
            <p>{rowData.notes}</p>
        </div>
    );
    const defaultCellRenderer = ({ cellData }) => {
        return <div title={cellData}>{cellData}</div>;
    };
    const combinedCellRenderer = ({ cellData, rowData }) => {
        return (
            <div>
                <div>{cellData ? formatShortDate(cellData) : ""}</div>
                <div title={rowData.period}>{rowData.period}</div>
            </div>
        );
    };

    function getWidthOfClass(className) {
        const elements = document.getElementsByClassName(className);
        return elements.length > 0 ? TABLE_WIDTH : 0;
    }
    const widthOfDiv = getWidthOfClass('report-table-container');

    const ManagerSigRenderer = ({ rowData }) => {
        if (!rowData.managerSignature) return null;
        return <img className="w-50" src={rowData.managerSignature} alt="Manager's Signature" />;
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearchTerm(value);
            }, 300),
        []
    );
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearchChange = useCallback(
        (e) => {
            const { value } = e.target;
            debouncedSearch(value);
        },
        [debouncedSearch]
    );

    const searchFilteredData = useMemo(() => {
        if (!searchTerm) return tableData;
        const lowercasedTerm = searchTerm.toLowerCase();
        return tableData.filter((item) => {
            return (
                (item.site && item.site.toLowerCase().includes(lowercasedTerm)) ||
                (item.provider && item.provider.toLowerCase().includes(lowercasedTerm)) ||
                (item.role && item.role.toLowerCase().includes(lowercasedTerm)) ||
                (item.notes && item.notes.toLowerCase().includes(lowercasedTerm))
            );
        });
    }, [tableData, searchTerm]);

    const handleSort = ({ sortBy: newSortBy }) => {
        let newSortDirection = SortDirection.ASC;
        if (sortBy === newSortBy) {
            newSortDirection =
                sortDirection === SortDirection.ASC
                    ? SortDirection.DESC
                    : SortDirection.ASC;
        }
        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
    };

    const sortedData = useMemo(() => {
        if (!sortBy) return searchFilteredData;
        const sorted = [...searchFilteredData].sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return aValue - bValue;
            }
            return aValue.toString().localeCompare(bValue.toString());
        });
        return sortDirection === SortDirection.ASC ? sorted : sorted.reverse();
    }, [searchFilteredData, sortBy, sortDirection]);

    useEffect(() => {
        visibleDataRef.current = sortedData;
    }, [sortedData]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const approveParam = params.get('approve');
        if (!hasOpenedModalRef.current && !showQueryFormPopup && approveParam) {
            const matchingRow = sortedData.find(row => row.lineId.toString() === approveParam);
            if (matchingRow) {
                onSignoffModal(matchingRow);
            }
        }
    }, [searchFilteredData]);

    const headerRenderer = ({ dataKey, label }) => {
        const isFirstSortableColumn = dataKey === label;
        const sortIndicator = dataKey === 'cat' ? <FaSort color="#CB2C84" /> : '';
        return (
            <div
                style={{ cursor: isFirstSortableColumn ? 'pointer' : 'default', userSelect: 'none' }}
                onClick={isFirstSortableColumn ? () => handleSort({ sortBy: dataKey }) : undefined}
            >
                {label}
                {sortIndicator}
            </div>
        );
    };

    return (
        <>
            {(
                isMobileOnly ?
                    <div>
                        {mobileFilters && (
                            <div className="mobile-menu">
                                <Filterstop
                                    onDatachange={onDataChange}
                                    DataBefore={reportData}
                                    DataAfter={DataAfterFilter}
                                    setLoading={setLoading}
                                    onSearchChange={handleSearchChange}
                                    filterValues={filterValues}
                                    setFilterValues={setFilterValues}
                                />
                            </div>
                        )}
                        {!mobileFilters && (
                            <div style={{ padding: '10px 0', width: '100%' }}>
                                <input
                                    type="text"
                                    className="table-search"
                                    placeholder="Search by Site, Provider, Role, or Notes..."
                                    onChange={handleSearchChange}
                                    style={{
                                        width: '100%',
                                        maxWidth: '300px',
                                        padding: '8px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        borderRadius: '4px',
                                        border: '1px solid #ced4da'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    :
                    <div>
                        <Filterstop
                            onDatachange={onDataChange}
                            DataBefore={reportData}
                            DataAfter={DataAfterFilter}
                            setLoading={setLoading}
                            onSearchChange={handleSearchChange}
                            filterValues={filterValues}
                            setFilterValues={setFilterValues}
                            columnConfig={columnConfig}
                            visibleColumns={costingVisibleCols}
                            onColumnsChange={setCostingVisibleCols}
                        />
                    </div>
            )}

            <RatingModal showModal={showRatingFeedback} onHide={ratingModalHide} lineid={feedbackdata.lineid} onSubmit={onRatingSubmit} rating={feedbackdata.rating} rl={feedbackdata.rl} />
            <AutoSizer>
                {({ height }) => (
                    <Table
                        width={widthOfDiv}
                        horizontal={true}
                        scrollingResetTimeInterval={0}
                        height={795}
                        rowHeight={ROW_HEIGHT}
                        rowRender={50}
                        rowCount={sortedData.length}
                        rowGetter={({ index }) => sortedData[index]}
                        sort={handleSort}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        rowStyle={({ index }) => rowHighlightStyle(sortedData, index)}
                        rowClassName={({ index }) => `costingTableRow ${index % 2 === 0 ? 'costingTableRow--odd' : 'costingTableRow--even'}`}
                    >
                        <Column width={40} minWidth={40} maxWidth={40} flexGrow={1} flexShrink={1} label="" className={"category-column"} dataKey="cat" cellRenderer={categoryCellRenderer} />
                        <Column width={40} minWidth={40} maxWidth={40} flexGrow={1} flexShrink={1} label="" className={"info-column"} dataKey="infoPanel" cellRenderer={infoCellRenderer} />
                        {showCol('invoiced') && <Column width={70} minWidth={70} label="Received" dataKey="invoiced" cellRenderer={dateCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('reportedToFinance') && <Column width={100} minWidth={100} label="Reported" dataKey="reportedToFinance" cellRenderer={combinedCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('jobdate') && <Column width={65} minWidth={65} label="Date" dataKey="jobdate" cellRenderer={dateCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('site') && <Column width={130} minWidth={130} label="Site" dataKey="site" cellRenderer={defaultCellRenderer} style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} headerRenderer={headerRenderer} />}
                        {showCol('provider') && <Column width={85} minWidth={85} style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} label="Staff" dataKey="provider" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('role') && <Column width={100} minWidth={100} className={"role-column"} label="Role" dataKey="role" cellRenderer={defaultCellRenderer} style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} headerRenderer={headerRenderer} />}
                        {featureFlags.copiaQueryApprove && showCol('gigpig') && (
                            <Column width={115} maxWidth={115} label="Approve/Challenge" style={{ display: 'flex', justifyContent: 'center' }} cellRenderer={gigPigCellRender} dataKey="gigpig" headerRenderer={headerRenderer} />
                        )}
                        {showCol('hours') && <Column width={45} minWidth={45} label="Hours" dataKey="hours" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('nettCost') && <Column width={75} minWidth={75} label="Net Cost" dataKey="nettCost" cellRenderer={nettCostCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('start') && <Column width={55} minWidth={55} label="Start" dataKey="start" cellRenderer={timeCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('finish') && <Column width={55} minWidth={55} label="Finish" dataKey="finish" cellRenderer={timeCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('managerSig') && <Column width={125} minWidth={125} label="Manager Signature" dataKey="managerSig" cellRenderer={ManagerSigRenderer} headerRenderer={headerRenderer} />}
                        {showCol('notes') && <Column width={320} minWidth={320} style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} label="Notes" dataKey="notes" cellRenderer={notesCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('rating') && <Column width={115} minWidth={115} label="Rating" dataKey="rating" cellRenderer={ratingCellRenderer} headerRenderer={headerRenderer} />}
                        {showCol('rw') && <Column width={48} minWidth={48} label="LineID" dataKey="rw" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                    </Table>
                )}
            </AutoSizer>
        </>
    );
};

ReportTableRow.propTypes = {
    data: PropTypes.array.isRequired,
    reportData: PropTypes.array.isRequired,
    featureFlags: PropTypes.object.isRequired,
    filterValues: PropTypes.object.isRequired,
    setFilterValues: PropTypes.func.isRequired,
    costingVisibleCols: PropTypes.array.isRequired,
    setCostingVisibleCols: PropTypes.func.isRequired,
    columnConfig: PropTypes.array.isRequired,
    onRatingSubmit: PropTypes.func.isRequired,
    onSignoffModal: PropTypes.func.isRequired,
    onQueryFormOpen: PropTypes.func.isRequired,
    onDataChange: PropTypes.func.isRequired,
    visibleDataRef: PropTypes.object.isRequired,
    prevFilteredDataRef: PropTypes.object.isRequired,
    updateLineItemRef: PropTypes.object.isRequired,
    setLoading: PropTypes.func.isRequired,
    DataAfterFilter: PropTypes.array.isRequired,
    mobileFilters: PropTypes.bool,
};

export default ReportTableRow;
